<template>
  <div>
    <div class="tabs flex border-b mb-4">
      <button
        class="py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue': currentTab === 'patient-queue',
          'text-gray-500': currentTab !== 'patient-queue',
        }"
        @click="currentTab = 'patient-queue'"
      >
        Physical Exam
      </button>
    </div>

    <form v-if="state === 'create' || state === 'edit'" @submit.prevent="submitForm">
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col">
          <label class="font-semibold text-sm" for="general">
            General <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.general"
            type="text"
            id="general"
            placeholder="Fair General Condition"
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.general">General is required</span>
        </div>

        <div class="flex flex-col">
          <label class="font-semibold text-sm" for="respiratory">
            Respiratory System <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.respiratory_system"
            type="text"
            id="respiratory"
            placeholder="NAD"
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.respiratory_system">Respiratory is required</span>
        </div>

        <div class="flex flex-col">
          <label class="font-semibold text-sm" for="cardiovascular">
            Cardiovascular System <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.cadiovascular_system"
            type="text"
            id="cardiovascular"
            placeholder="Type..."
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.cadiovascular_system">Cardiovascular is required</span>
        </div>

        <div class="flex flex-col">
          <label class="font-semibold text-sm" for="cns">
            Central Nervous System <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.cns"
            type="text"
            id="cns"
            placeholder="Type..."
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.cns">CNS is required</span>
        </div>

        <div class="flex flex-col">
          <label class="font-semibold text-sm" for="abdomen">
            Abdomen <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.abdomen"
            type="text"
            id="abdomen"
            placeholder="Transplant scars noted"
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.abdomen">Abdomen is required</span>
        </div>

        <div class="flex flex-col">
          <label class="font-semibold text-sm" for="genitourinary">
            Genitourinary <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.genitourinary"
            type="text"
            id="genitourinary"
            placeholder="Type..."
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.genitourinary">Genitourinary is required</span>
        </div>

        <div class="flex flex-col">
          <label class="font-semibold text-sm" for="ent">
            Ear, Nose & Throat <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.ent"
            type="text"
            id="ent"
            placeholder="Type..."
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.ent">ENT is required</span>
        </div>

        <div class="flex flex-col">
          <label class="font-semibold text-sm" for="skin">
            Skin <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.skin"
            type="text"
            id="skin"
            placeholder="Type..."
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.skin">Skin is required</span>
        </div>

        <div class="flex flex-col col-span-2">
          <label class="font-semibold text-sm" for="musculoskeletal">
            Musculoskeletal <span v-if="!optional" class="text-red-500">*</span>
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.musculoskeletal"
            type="text"
            id="musculoskeletal"
            placeholder="Type..."
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
          <span class="text-red-700 text-sm" v-if="errors.musculoskeletal">Musculoskeletal is required</span>
        </div>

        <div class="flex flex-col col-span-2">
          <label class="font-semibold text-sm" for="other">
            Other
          </label>
          <input
            :disabled="state === 'view'"
            v-model="physicalExamForm.other"
            type="text"
            id="other"
            placeholder="Type..."
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
        </div>
      </div>
    </form>

    <div v-if="state === 'view'" class="grid grid-cols-2 px-4 gap-4 mb-6">
      <div>
        <p class="font-semibold">General:</p>
        <p>{{ existingPhysicalExam.general ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Respiratory System:</p>
        <p>{{ existingPhysicalExam.respiratory_system ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Cardiovascular System:</p>
        <p>{{ existingPhysicalExam.cadiovascular_system ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Central Nervous System:</p>
        <p>{{ existingPhysicalExam.cns ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Abdomen:</p>
        <p>{{ existingPhysicalExam.abdomen ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Genitourinary:</p>
        <p>{{ existingPhysicalExam.genitourinary ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Ear, Nose & Throat:</p>
        <p>{{ existingPhysicalExam.ent ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Skin:</p>
        <p>{{ existingPhysicalExam.skin ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Musculoskeletal:</p>
        <p>{{ existingPhysicalExam.musculoskeletal ?? "-" }}</p>
      </div>
      <div>
        <p class="font-semibold">Other:</p>
        <p>{{ existingPhysicalExam.other ?? "-" }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PhysicalExam",
  props: {
    state: {
      type: String,
      required: true,
    },
    visitId: {
      type: String,
      required: true,
    },
    existingPhysicalExam: {
      type: Object,
      default: () => ({}),
    },
    optional: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentTab: "patient-queue",
      errors: {},
      physicalExamForm: {
        general: "",
        respiratory_system: "",
        cadiovascular_system: "",
        cns: "",
        abdomen: "",
        genitourinary: "",
        ent: "",
        skin: "",
        musculoskeletal: "",
        other: "",
      },
    };
  },
  watch: {
    existingPhysicalExam: {
      handler(newItem) {
        if (newItem && Object.keys(newItem).length > 0) {
          this.physicalExamForm = {
            ...this.physicalExamForm,
            ...newItem,
            general:              newItem.general              || "",
            respiratory_system:   newItem.respiratory_system   || "",
            cadiovascular_system: newItem.cadiovascular_system || "",
            cns:                  newItem.cns                  || "",
            abdomen:              newItem.abdomen              || "",
            genitourinary:        newItem.genitourinary        || "",
            ent:                  newItem.ent                  || "",
            skin:                 newItem.skin                 || "",
            musculoskeletal:      newItem.musculoskeletal      || "",
            other:                newItem.other                || "",
          };
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    submitPhysicalExam() {
      if (this.optional) {
        this.errors = {};
        return { ...this.physicalExamForm };
      }
      if (!this.validPhysicalExam()) {
        return {};
      }
      return { ...this.physicalExamForm };
    },
    validPhysicalExam() {
      if (this.optional) return true;

      this.errors = {};
      let isValid = true;
      const fields = [
        { key: "general",             message: "General is required"         },
        { key: "respiratory_system",  message: "Respiratory is required"     },
        { key: "cadiovascular_system",message: "Cardiovascular is required"  },
        { key: "cns",                 message: "CNS is required"             },
        { key: "abdomen",             message: "Abdomen is required"         },
        { key: "genitourinary",       message: "Genitourinary is required"   },
        { key: "ent",                 message: "ENT is required"             },
        { key: "skin",                message: "Skin is required"            },
        { key: "musculoskeletal",     message: "Musculoskeletal is required" },
        // "other" is intentionally not validated — always optional
      ];
      fields.forEach(field => {
        if (!this.physicalExamForm[field.key]) {
          this.errors[field.key] = field.message;
          isValid = false;
        }
      });
      return isValid;
    },
  },
};
</script>